import * as React from "react";

import { Box } from "@material-ui/core";

import { FormBuilder } from "client/domain/FormBuilder/FormBuilder";
import {
  useFeatureFlags,
  useLead,
  useMerchantModel,
  useTranslation,
  useUiFieldMap,
} from "client/hooks";
import { logAmplitudeEvent } from "client/lib/amplitude";

import { BaseConversationalLayout } from "./BaseConversationalLayout";
import {
  configs,
  configVariantA,
  uiFieldMaps,
  uiFieldMapVariantA,
} from "./config";
import { SubmitButtons } from "./SubmitButtons";

export const OneQAAT: React.FC = () => {
  const [allFlags] = useFeatureFlags();
  const variant = allFlags.merchantPortalExperimentRedesignSignUp;
  const [activePageIndex, setActivePageIndex] = React.useState(0);
  const { validateCustomFields, merchant } = useMerchantModel();
  const { orderedUiFieldMap, updateUiFieldMap } = useUiFieldMap(
    uiFieldMaps[variant] || uiFieldMapVariantA
  );
  const [activeConfig, setActiveConfig] = React.useState(
    configs[variant] || configVariantA
  );

  React.useEffect(() => {
    const uiFieldMap = uiFieldMaps[variant];
    const config = configs[variant];
    if (uiFieldMap) {
      updateUiFieldMap(uiFieldMap);
    }
    if (config) {
      setActiveConfig(config);
    }
  }, [variant]);

  const routeToEarliestValidationError = (
    validationErrors: Record<string, string>
  ) => {
    const fieldErrorKeys = Object.keys(validationErrors);
    if (!fieldErrorKeys.length) {
      return;
    }
    let earliestPageWithErrors = activeConfig.pages.length; // initialize to number greater than last index of pages
    const flattenedFieldMap = Object.entries(
      orderedUiFieldMap.lead as Record<string, any>
    );
    for (const [fieldProperty, fieldConfig] of flattenedFieldMap) {
      // if we find a field with an error, set the earliest page with errors to the page number of the field
      if (fieldErrorKeys.includes(fieldProperty)) {
        earliestPageWithErrors = Math.min(
          earliestPageWithErrors,
          fieldConfig?.pageNumber ?? Number.MAX_SAFE_INTEGER
        );
      }
    }
    if (earliestPageWithErrors < activeConfig.pages.length) {
      // since page number starts at 1, subtract 1 to get the index of the page
      setActivePageIndex(earliestPageWithErrors - 1);
    }
  };

  const { handleSubmit } = useLead(routeToEarliestValidationError);
  const t = useTranslation();

  const getShownFieldProperties = (els: HTMLFormControlsCollection) => {
    const inputs = Array.from(els).filter((el) => el.nodeName === "INPUT");
    // radio input id's have -yes/-no suffixes
    const fieldProperties = inputs.map((el) => el.id.replace(/-(yes|no)/i, ""));
    return fieldProperties;
  };

  const customValidator = (e: React.FormEvent<HTMLFormElement>): boolean => {
    e.preventDefault();
    e.stopPropagation();
    const fieldProperties = getShownFieldProperties(e.currentTarget.elements);
    return validateCustomFields(fieldProperties, merchant.lead);
  };

  const pageCompletionTracker = React.useRef(
    activeConfig.pages.map(() => false)
  );

  const capturePageCompletion = (pageIndex: number) => {
    if (!pageCompletionTracker.current[pageIndex]) {
      pageCompletionTracker.current[pageIndex] = true;
      logAmplitudeEvent("REDESIGN_SIGN_UP", "completed_one_qaat_page", {
        page_name: activeConfig.pages[pageIndex].name,
        current_page_completed: pageIndex + 1,
        total_pages_completed: pageCompletionTracker.current.filter(Boolean)
          .length,
        total_page_count: activeConfig.pages.length,
      });
    }
  };

  const handleNextPage = (e: React.FormEvent<HTMLFormElement>) => {
    capturePageCompletion(activePageIndex);
    if (activePageIndex === activeConfig.pages.length - 1) {
      // final page, submit to server
      handleSubmit(e);
    } else {
      setActivePageIndex(activePageIndex + 1);
    }
  };

  const handlePrevPage = () => {
    if (activePageIndex > 0) {
      setActivePageIndex(activePageIndex - 1);
    }
  };

  const conversationalPages = activeConfig.pages.map((page) => (
    <BaseConversationalLayout
      key={page.name}
      heading={t(page.headingCopyKey)}
      subHeading={
        page?.subHeadingCopyKey ? t(page.subHeadingCopyKey) : undefined
      }
      form={
        <FormBuilder
          fieldSet={merchant.lead}
          currentConversationalPage={activePageIndex + 1}
          customValidator={customValidator}
          customSubmitComponent={
            <SubmitButtons
              handleBack={activePageIndex > 0 ? handlePrevPage : undefined}
            />
          }
          onSubmit={handleNextPage}
          uiFieldMap={orderedUiFieldMap.lead}
        />
      }
    />
  ));
  return <Box>{conversationalPages[activePageIndex]}</Box>;
};
